import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/shared/hooks/useAuth";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { supabase } from "@/shared/integrations/supabase/client";

/**
 * Verifica se o usuário pode alimentar bases de IA.
 * Admin sempre tem permissão. Caso contrário, consulta ai_alimentacao_permissoes.
 * module="*" = qualquer permissão (hub)
 */
export function useAlimentacaoPermissao(scope: "alimentar_geral" | "alimentar_modulo", module?: string) {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const isAdmin = hasPermission("administracao", "visualizar");

  const { data: hasPerm = false, isLoading } = useQuery({
    queryKey: ["alimentacao-permissao", user?.id, scope, module ?? ""],
    queryFn: async () => {
      if (!user?.id) return false;
      if (isAdmin) return true;

      const moduleVal = scope === "alimentar_modulo" ? (module ?? "") : "";

      if (moduleVal === "*") {
        const { data, error } = await supabase
          .from("ai_alimentacao_permissoes")
          .select("id")
          .eq("user_id", user.id)
          .limit(1);
        if (error) return false;
        return (data?.length ?? 0) > 0;
      }

      const { data, error } = await supabase
        .from("ai_alimentacao_permissoes")
        .select("scope, module")
        .eq("user_id", user.id);

      if (error) return false;

      const perms = data ?? [];
      const hasGeral = perms.some((p) => p.scope === "alimentar_geral" && p.module === "");
      const hasModulo = perms.some((p) => p.scope === "alimentar_modulo" && p.module === moduleVal);

      if (scope === "alimentar_geral") return hasGeral;
      return hasGeral || hasModulo;
    },
    enabled: !!user?.id,
  });

  return { podeAlimentar: isAdmin || hasPerm, isLoading };
}
